import React, { useEffect, useState, useMemo, useRef } from 'react';
import { MessageSquare, X, User } from 'lucide-react';
import { Button } from 'components/ui/button';
import dayjs from 'dayjs';
import { getPendingPodNotes, createPendingPodNote, type PendingPodNote } from '../api';

interface Props {
  cnNumber: string;
}

export const NotesModal: React.FC<Props> = ({ cnNumber }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [notes, setNotes] = useState<PendingPodNote[]>([]);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [isOpen, notes]);

  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed.ecode || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const loadNotes = async () => {
      try {
        setLoading(true);
        const data = await getPendingPodNotes(cnNumber);
        setNotes(data.notes || []);
      } catch (err) {
        console.error('Failed to load notes', err);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [isOpen, cnNumber]);

  const handleSend = async () => {
    if (!message.trim() || loading) return;

    try {
      setLoading(true);
      await createPendingPodNote(cnNumber, {
        content: message.trim(),
      });

      const data = await getPendingPodNotes(cnNumber);
      setNotes(data.notes || []);
      setMessage('');
    } catch (err) {
      console.error('Failed to send note', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-5 px-1.5 py-0 bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200"
        onClick={() => setIsOpen(true)}
      >
        <MessageSquare className="h-3 w-3" />
      </Button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="w-[600px] max-w-[90vw] h-[600px] bg-white rounded-xl shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 rounded-t-xl">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Pending POD Notes</h3>
                <p className="text-xs text-gray-500">CN: {cnNumber}</p>
              </div>
              <button onClick={() => setIsOpen(false)} className="p-1 rounded-md hover:bg-gray-200 transition">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto scroll-smooth px-5 py-4 space-y-4 bg-[#f8fafc]">
              {loading && <div className="text-center text-xs text-gray-400">Loading notes…</div>}

              {!loading && notes.length === 0 && (
                <div className="flex h-full items-center justify-center">
                  <span className="text-xs text-gray-400">No notes yet</span>
                </div>
              )}

              {notes.map((note, idx) => {
                const isSelf = currentUser && note.created_by_ecode === currentUser;

                return (
                  <div key={idx} className={`flex items-start gap-2 ${isSelf ? 'justify-end' : 'justify-start'}`}>
                    {!isSelf && (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-3.5 overflow-hidden">
                        {note.photo_url ? (
                          <img src={note.photo_url} alt={note.created_by_name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    )}

                    <div className={`flex flex-col ${isSelf ? 'items-end text-right' : 'items-start text-left'}`} style={{ maxWidth: '75%' }}>
                      <span className={`text-[10px] mb-0.5 px-1 leading-normal ${isSelf ? 'text-blue-500' : 'text-gray-500'}`}>
                        {isSelf ? 'You' : note.created_by_name}
                      </span>
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-xs shadow-sm text-left ${
                          isSelf ? 'bg-blue-500 text-white' : 'bg-white text-gray-800 border border-gray-200'
                        }`}
                      >
                        <p className="whitespace-pre-wrap leading-relaxed text-left">{note.content}</p>
                        <div className="mt-1 text-[10px] opacity-70 text-right w-full">
                          {dayjs(note.created_at).format('DD MMM, HH:mm')}
                        </div>
                      </div>
                    </div>

                    {isSelf && (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-3.5 overflow-hidden">
                        {note.photo_url ? (
                          <img src={note.photo_url} alt="You" className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-blue-500" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t bg-white px-4 py-3 flex items-center gap-2 rounded-b-xl">
              <input
                className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-xs
                           focus:outline-none focus:ring-2 focus:ring-blue-400
                           placeholder-gray-400 disabled:opacity-50"
                placeholder="Type a note…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                disabled={loading}
              />
              <Button size="sm" className="rounded-full px-4" onClick={handleSend} disabled={loading || !message.trim()}>
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
